/**
 * Link odzyskania porzuconego koszyka z maila SALESmanago:
 * `/cart-recover?cart=<id_cart>&token=<skrót>&smclient=<kontakt>`.
 *
 * Walidacja wspólna dla strony (parametry z adresu) i trasy Nitro (body żądania),
 * żeby obie warstwy odrzucały ten sam uszkodzony link, zanim dotrze do backendu.
 * Leaf-moduł bez zależności — testowalny w środowisku node i importowalny
 * relatywnie z service workera (app/sw/sw.ts).
 */

/** Ścieżka strony odzyskania koszyka. Jej adres niesie token koszyka. */
export const CART_RECOVER_PATH = '/cart-recover'

/**
 * Parametry linku, które stanowią sekret (koszyk + token). Nie mogą trafić do
 * trackerów ani cache; pozostałe (`smclient`, `utm_*`) są zwykłymi parametrami
 * kampanii.
 */
export const CART_RECOVER_SECRET_PARAMS = ['cart', 'token'] as const

/**
 * Czy ścieżka (bez query) to strona odzyskania koszyka. Toleruje końcowy ukośnik
 * i wielkość liter — router dopasowuje trasy bez rozróżniania wielkości liter.
 */
export function isCartRecoverPath(path: string): boolean {
  return path.toLowerCase().replace(/\/+$/, '') === CART_RECOVER_PATH
}

/**
 * Backend liczy token jako HMAC-SHA256 (64 znaki hex, małe litery) i porównuje go
 * dosłownie — wielkie litery odrzuca, więc front też. O dokładnej długości rozstrzyga
 * backend (zmiana algorytmu nie wymaga wydania frontu); front odrzuca od razu tylko
 * to, co na pewno nie jest skrótem (ucięty link, śmieci).
 */
export const CART_RECOVER_TOKEN_PATTERN = /^[a-f0-9]{32,128}$/

export interface CartRecoverParams {
  cart: number
  token: string
}

/** Dodatnia liczba całkowita z liczby albo ciągu cyfr; inaczej `undefined`. */
function readCartId(value: unknown): number | undefined {
  const candidate = typeof value === 'string' && /^\d+$/.test(value.trim())
    ? Number(value.trim())
    : value

  return typeof candidate === 'number' && Number.isSafeInteger(candidate) && candidate > 0
    ? candidate
    : undefined
}

/** Parametry linku albo `null`, gdy link jest uszkodzony (np. ucięty przez klienta poczty). */
export function readCartRecoverParams(cart: unknown, token: unknown): CartRecoverParams | null {
  const idCart = readCartId(cart)
  const normalizedToken = typeof token === 'string' ? token.trim() : ''

  if (idCart === undefined || !CART_RECOVER_TOKEN_PATTERN.test(normalizedToken)) {
    return null
  }

  return { cart: idCart, token: normalizedToken }
}
